import { LitElement, html, css, t, paths, unsafeHTML } from  '@hh/global.js'

class HHBundleModal extends LitElement {
  static properties = {
    product: { type: Object },
    currentStep: { type: Number },
    maxStepQty: { type: Number },
    bundle: {
      type: Array,
    },
    _activeTab: {
      state: true,
    },
    qty: {
      state: true,
    },
    scrollOffset: {
      state: true,
    },
  }

  constructor() {
    super()
    this._activeTab = 0
    this.qty = 1;
    this.onKeyUp = this.onKeyUp.bind(this);
  }

  get isAdded() {
    return this.bundle[this.currentStep].some((item) => item.variantId === this.product.variantId);
  }

  get initQty() {
    return this.bundle[this.currentStep].find((item) => item.variantId === this.product.variantId)?.qty || 1;
  }

  get maxQty() {
    return this.maxStepQty - this.bundle[this.currentStep].reduce((acc, item) => {
      if (item.variantId !== this.product.variantId) {
        acc += item.qty;
      }
      return acc;
    }, 0) || 1;
  }

  updateBundle(qty) {
    this.dispatchEvent(new CustomEvent('updateBundle', {
      detail: {
        variantId: this.product.variantId,
        qty: qty,
      },
      bubbles: true,
      composed: true
    }));
  }

  onAdd() {
    this.updateBundle(this.qty);
  }

  onRemove() {
    this.updateBundle(0);
  }

  close() {
    this.dispatchEvent(new CustomEvent('close', {
      bubbles: true,
      composed: true
    }));
  }

  onChange(event) {
    this.qty = event.detail.value;
    if (this.isAdded) {
      this.updateBundle(this.qty);
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() === 'ESCAPE') {
      this.close();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const { scrollY } = window;
    document.body.classList.add('disabled-scroll');
    this.scrollOffset = scrollY;
    document.body.style.top = `-${scrollY}px`;
    window.addEventListener('keyup', this.onKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.classList.remove('disabled-scroll');
    document.body.style.top = `0`;
    window.scroll({
      top: this.scrollOffset,
      left: 0,
      behavior: "instant",
    });
    window.removeEventListener('keyup', this.onKeyUp);
  }

  render() {
    const tabs = [
      {
        title: t('products.product.description'),
        content: this.product.description,
      },
      {
        title: t('products.product.accordion_content'),
        content: this.product.content,
      },
      {
        title: t('products.product.accordion_additional_information'),
        content: this.product.info,
      },
    ]
    return html`
      <div class="bundle-modal__backdrop" @click="${this.close}"></div>
      <div class="bundle-modal">
        <div class="bundle-modal__body">
          <div class="bundle-modal__gallery">
            <button
              @click="${this.close}"
              class="bundle-modal__close"
              type="button"
              aria-label="${t('accessibility.close')}"
            >
              ${unsafeHTML(paths.close)}
            </button>
            <hh-slider
              class="product-media__grid"
              breakpoint="1024"
              active="true"
            >
              ${this.product.images.map((image, index) => html`
                <div
                  role="listitem"
                  slot="item"
                  class="bundle-modal__slide"
                  data-media-item="${index}"
                >
                  <div class="bundle-modal__media">
                      <img src=${image} class="bundle-modal__image"/>
                      <div class="bundle-modal__media-index">${index + 1}/${this.product.images.length}</div>
                  </div>
                </div>
              `)}
            </hh-slider>
          </div>
          <div class="bundle-modal__content">
            <h2 class="bundle-modal__title">${this.product.title}</h2>
            <div class="bundle-modal__sku">
              ${this.product.catalog_number}
            </div>
            <div class="bundle-modal__price">
              ${this.product.sale_price ? html`
                <div class="price--sale">${this.product.price}</div>
                <div class="price">${this.product.sale_price}</div>
              ` : html`
                <div class="price--regular">${this.product.price}</div>
              `}
            </div>
            <div class="bundle-modal__tabs">
              ${tabs.map((tab, index) => html`
              <button class="bundle-modal__tab ${index === this._activeTab ? 'active' : ''}" @click="${() => this._activeTab = index}">
                ${tab.title}
              </button>
            `)}
            </div>
            <div class="bundle-modal__tab-content">
              ${unsafeHTML(tabs[this._activeTab].content)}
            </div>
            <div class="bundle-modal__params">
              <div class="bundle-modal__param">
                <div class="bundle-modal__param-title">
                  ${t('products.product.ages')}:
                </div>
                <div class="bundle-modal__param-text">
                  ${this.product.ages}
                </div>
              </div>
              <div class="bundle-modal__param">
                <div class="bundle-modal__param-title">
                  ${t('products.product.dimensions')}:
                </div>
                <div class="bundle-modal__param-text">
                  ${this.product.dimensions}
                </div>
              </div>
              <div class="bundle-modal__param">
                <div class="bundle-modal__param-title">
                  ${t('products.product.pieces')}:
                </div>
                <div class="bundle-modal__param-text">
                  ${this.product.pieces}
                </div>
              </div>
            </div>
            <div class="bundle-modal__controls">
              <hh-quantity-input
                .initial='${this.initQty}'
                min='1'
                .max="${this.maxQty}"
                .key="${this.product.variantId}"
                @changeQuantity="${this.onChange}"
              >
                <button type="button" name="minus" slot="decrease">
                  ${unsafeHTML(paths.minus)}
                </button>
                <button type="button" name="plus" slot="increase">
                  ${unsafeHTML(paths.plus)}
                </button>
              </hh-quantity-input>
              ${this.isAdded ? html`
                <hh-button
                  class="w1"
                  theme="secondary"
                  full-width
                  @click="${this.onRemove}"
                >
                  ${t('cart.general.remove')}
                </hh-button>
              ` : html`
                <hh-button
                  class="w1"
                  theme="primary"
                  custom-color='var(--hh-color-brand-primary)'
                  full-width
                  @click="${this.onAdd}"
                >
                  ${t('products.product.add_to_cart')}
                </hh-button>
              `}
              </div>
          </div>
        </div>
      </div>
    `
  }

  static styles = css
    `
    .bundle-modal__backdrop {
      z-index: 100001;
      position: fixed;
      top: 0;
      right: 0;
      width: 100%;
      height: 100%;
      background: rgba(var(--hh-color-night-rgb), 0.5)
    }
    .bundle-modal {
      z-index: 100001;
      position: fixed;
      top: 0;
      right: 0;
      width: 96%;
      height: 100%;
      max-width: 35.8rem;
      background: var(--hh-color-day);
      display: flex;
      flex-direction: column;
      padding-bottom: 9.3rem;
      box-sizing: border-box;
    }

    .bundle-modal__body {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }

    .bundle-modal__gallery {
      position: relative;
    }

    .bundle-modal__media {
      width: 100%;
      padding-top: 100%;
      position: relative;
    }
    .bundle-modal__image{
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: contain;
      top: 0;
      left: 0;
    }
    hh-slider {
      --hh-background: var(--hh-color-light);
      --hh-button-background: var(--hh-color-day);
      --hh-button-border: 1px solid var(--hh-color-neutral);
      --hh-cell-width: 100%;
    }
    hh-slider:not([open='true']) {
      --hh-container-flex: flex;
      --hh-container-flex-wrap: wrap;
    }
    .bundle-modal__slide {
      flex-grow: 1;
      flex-shrink: 0;
      box-sizing: border-box;
      width: 100% !important;
      max-width: 100% !important;
      scroll-snap-align: start;
    }
    .bundle-modal__content {
      padding: var(--hh-spacing-xl) var(--hh-spacing-md);
      border-top: 0.05rem solid var(--hh-color-mid-grey);
    }
    .bundle-modal__title{
      font: var(--hh-h2-font);
      margin: 0 0 var(--hh-spacing-xxs);
      color: var(--hh-color-night);
    }
    .bundle-modal__sku {
      font: var(--hh-body-font);
      letter-spacing: var(--hh-h-letter-spacing);
      margin: 0 0 var(--hh-spacing-xxs);
    }
    .bundle-modal__price{
      display: flex;
      gap: var(--hh-spacing-xs);
      letter-spacing: var(--hh-body-sm-letter-spacing);
      margin-bottom: var(--hh-spacing-2xl);
    }
    .price{
        color: var(--hh-color-brand-primary);
    }
    .price--sale{
        text-decoration: line-through;
    }
    .bundle-modal__tabs {
      display: flex;
      gap: var(--hh-spacing-md);
    }
    .bundle-modal__tab {
      font: var(--hh-body-bold-xs-font);
      letter-spacing: var(--hh-body-xs-letter-spacing);
      color: var(--hh-color-mid-grey);
      background: none;
      border: none;
      border-bottom: 0.1rem solid transparent;
      padding: var(--hh-spacing-xs) 0;
    }
    .bundle-modal__tab.active {
      border-color: var(--hh-color-night);
      color: var(--hh-color-night);
    }
    .bundle-modal__tab-content{
      padding: var(--hh-spacing-lg) 0;
      font: var(--hh-body-xs-font);
      letter-spacing: var(--hh-body-xs-letter-spacing);
      color: var(--hh-color-brand-subdued);
    }
    .bundle-modal__tab-content p {
      margin: 0;
    }
    .bundle-modal__params {
      display: flex;
      flex-wrap: wrap;
      font: var(--hh-body-xs-font);
      letter-spacing: var(--hh-body-xs-letter-spacing);
    }
    .bundle-modal__param{
      display: flex;
      gap: var(--hh-spacing-xxs);
    }
    .bundle-modal__param:first-child {
      padding-right: var(--hh-spacing-sm);
      border-right: 0.1rem solid var(--hh-color-mid-grey);
    }
    .bundle-modal__param:nth-child(2) {
      flex: 1;
      text-align: center;
      padding: 0 var(--hh-spacing-xxs);
      justify-content: center;
    }
    .bundle-modal__param:last-child {
      padding-left: var(--hh-spacing-sm);
      border-left: 0.1rem solid var(--hh-color-mid-grey);
    }
    .bundle-modal__param-title {
      color: var(--hh-color-night);
    }
    .bundle-modal__param-text {
      color: var(--hh-color-brand-subdued);
    }
    .bundle-modal__controls {
      display: flex;
      align-items: center;
      padding: var(--hh-spacing-xl) var(--hh-spacing-md);
      gap: var(--hh-spacing-md);
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      box-sizing: border-box;
    }
    hh-button {
      flex: 1;
      --hh-width: 100%;
      --hh-height: 4.5rem;
      --hh-max-height: 4.5rem;
      --hh-font: var(--hh-body-bold-sm-font);
      --hh-border-width: 0.5rem;
    }
    hh-button[theme="secondary"] {
      --hh-color: var(--hh-color-brand-secondary);
      --hh-border-color: var(--hh-color-brand-secondary);
    }
    .bundle-modal__close{
      z-index: 10;
      width: 3rem;
      height: 3rem;
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 1.1rem;
      right: 1.2rem;
      border-radius: 50%;
      background: var(--hh-color-day);
      border: none;
      color: var(--hh-color-night);
      box-shadow: 0.4rem 0.4rem 20rem 0 rgba(var(--hh-color-night-rgb), 0.08);
    }
    .bundle-modal__close svg{
      width: 1rem;
      height: 1rem;
    }
    .bundle-modal__media-index {
      display: none;
    }
    @media (min-width: 1024px) {
      .bundle-modal {
        width: 90%;
        max-width: 123.1rem;
        padding-bottom: 0;
      }
      .bundle-modal__body {
        display: flex;
        position: relative;
      }
      .bundle-modal__content {
        max-width: 48rem;
        flex: none;
        padding: var(--hh-spacing-3xl);
        box-sizing: border-box;
        position: sticky;
        top: 0;
      }
      .bundle-modal__gallery {
        flex: 1;
        border-right: 0.1rem solid var(--hh-color-mid-grey);
        height: fit-content;
        position: static;
      }
      .bundle-modal__controls {
        position: static;
        padding: var(--hh-spacing-3xl) 0 var(--hh-spacing-md);
      }
      .bundle-modal__title {
        font: var(--hh-h3-font);
      }
      .bundle-modal__sku {
        margin-bottom: var(--hh-spacing-md);
      }
      .bundle-modal__price {
        margin-bottom: var(--hh-spacing-3xl);
      }
      .bundle-modal__tab-content {
        padding: var(--hh-spacing-xl) 0;
      }
      .bundle-modal__close {
        right: 2.8rem;
        top: 1.2rem;
      }
      .bundle-modal__media-index {
        display: block;
        position: absolute;
        bottom: 2.9rem;
        right: 2.5rem;
        font: var(--hh-body-font);
        color: var(--hh-color-mid-grey);
      }
    }
  `
}
customElements.define('hh-bundle-modal', HHBundleModal)
